interface GlobalDraftPromptArgs {
    isGlobalDraftEnabled: boolean;
    serverDraftId?: string;
    serverDraftVersion?: string | null;
    loadedDraftId?: string | null;
    loadedDraftVersion?: string | null;
    hasServerContent: boolean;
}

interface DraftRange {
    id: string;
    start?: string;
    end?: string;
}

interface PayrollCycleDraftValidationArgs {
    isGlobalDraftEnabled: boolean;
    previousPayrollStart?: string | null;
    nextPayrollStart?: string | null;
}

const latestDraftMessage = "Latest Draft Version is available to load";

const getStorageKeyPart = (value: string | undefined) => value?.trim() || "unknown";
const getLoadedGlobalDraftKey = (siteId: string, userId: string | undefined) =>
    `global_draft_${getStorageKeyPart(siteId)}_${getStorageKeyPart(userId)}`;
const getLoadedGlobalDraftVersionKey = (siteId: string, userId: string | undefined) =>
    `${getLoadedGlobalDraftKey(siteId, userId)}_version`;

const shouldShowGlobalDraftAvailablePrompt = ({
    isGlobalDraftEnabled,
    serverDraftId,
    serverDraftVersion,
    loadedDraftId,
    loadedDraftVersion,
    hasServerContent,
}: GlobalDraftPromptArgs) => {
    const hasSameLoadedDraft = serverDraftId === loadedDraftId
        && (!serverDraftVersion || serverDraftVersion === loadedDraftVersion);

    return isGlobalDraftEnabled
        && hasServerContent
        && !!serverDraftId
        && !hasSameLoadedDraft;
}

const isDateWithinDraftRange = (date: string | undefined, draft: DraftRange) => {
    return !!date
        && !!draft.start
        && !!draft.end
        && date >= draft.start
        && date <= draft.end;
}

const findDraftForPayrollRange = <TDraft extends DraftRange>(
    drafts: TDraft[],
    visibleStart: string,
    payrollStart?: string,
) => {
    return drafts.find(draft => {
        return draft.start === visibleStart
            || (!!payrollStart && draft.start === payrollStart)
            || isDateWithinDraftRange(visibleStart, draft)
            || isDateWithinDraftRange(payrollStart, draft);
    });
}

const shouldValidateGlobalDraftOnPayrollCycleChange = ({
    isGlobalDraftEnabled,
    previousPayrollStart,
    nextPayrollStart,
}: PayrollCycleDraftValidationArgs) => {
    return isGlobalDraftEnabled
        && !!nextPayrollStart
        && previousPayrollStart !== nextPayrollStart;
}

const createLoadDraftPromptOptions = (isGlobalDraftEnabled: boolean) => {
    if (isGlobalDraftEnabled) {
        return {
            text: latestDraftMessage,
            icon: "info" as const,
            showDenyButton: false,
            confirmButtonText: "Load Draft",
            confirmButtonColor: "green",
        };
    }

    return {
        title: "Load Draft?",
        text: "Do you want to load the saved draft?",
        icon: "info" as const,
        showDenyButton: true,
        confirmButtonText: "Yes",
        denyButtonText: "No",
        confirmButtonColor: "green",
        denyButtonColor: "red",
    };
}

export {
    createLoadDraftPromptOptions,
    findDraftForPayrollRange,
    getLoadedGlobalDraftKey,
    getLoadedGlobalDraftVersionKey,
    shouldValidateGlobalDraftOnPayrollCycleChange,
    shouldShowGlobalDraftAvailablePrompt,
};
